import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  Typography,
  Grid,
} from "@mui/material";

const TOPICS = [
  // Technology and Trends
  "Artificial Intelligence", "Machine Learning", "Blockchain","Stocks", "Cryptocurrency", "Cybersecurity",
  "Big Data", "Data Science", "Internet of Things", "Augmented Reality", "Virtual Reality",
  "Quantum Computing", "Renewable Energy", "Climate Change", "Sustainability", "Electric Vehicles",
  "Space Exploration", "Smart Cities", "5G", "Wearable Technology", "Healthcare Innovation",

  // Sport
  "Sport", "Soccer", "Football", "Basketball", "Rugby", "Tennis", "Paddle",

  // Personal Development and Lifestyle
  "Mental Health", "Fitness", "Nutrition",
  "Self-Improvement", "Mindfulness",
  "Meditation", "Yoga", "Travel", "Sustainable Fashion", "Books",

  // Digital Platforms and Entertainment
  "E-Commerce", "Gaming", "Streaming Platforms", "Netflix", "YouTube",
  "TikTok", "Music Streaming", "Spotify", "Digital Marketing", "Social Media Marketing",

  // Career and Skills
  "Remote Work", "Freelancing", "Entrepreneurship", "Leadership", "Time Management",
  "Programming Languages", "Python", "JavaScript", "UX/UI Design", "Graphic Design",

  // Popular Household Items
  "Vacuum Cleaners", "Microwaves", "Blenders", "Air Fryers", "Coffee Makers",
  "Refrigerators", "Washing Machines", "Dishwashers", "Mattresses", "Pillows", "Sofa", "Chair",

  // Popular Clothing Items
  "T-Shirts", "Jeans",
  "Sneakers", "Jackets", "Sweaters",
  "Hoodies", "Dresses", "Blouses", "Formal Suits", "Shorts",

  // Everyday Life and Society
  "Recycling", "Human Rights", "Diversity and Inclusion", "Parenting", "Work-Life Balance",
  "Event Planning", "Community Building", "Networking", "Personal Finance", "Investing",

  // Emerging Topics and Innovations
  "3D Printing", "Robotics", "Smart Home Devices", "Voice Assistants", "Electric Kettles",
  "Space Tourism", "AI Ethics", "Green Technology", "Wildlife Protection", "Ocean Cleanup",

  // Food and Cooking
  "Cooking", "Baking", "World Cuisines", "Nonstick Pans", "Cutlery Sets"
];

const InterestsDialog = ({ open, onClose, onSave, initialInterests = [], isRequired = false }) => {
  const [selectedInterests, setSelectedInterests] = useState(
    initialInterests || []
  );

  // Reset selected interests when dialog opens or initialInterests changes
  React.useEffect(() => {
    if (open) {
      setSelectedInterests(initialInterests || []);
    }
  }, [open, initialInterests]);

  const handleToggleInterest = (topic) => {
    setSelectedInterests((prev) => {
      if (prev.includes(topic)) {
        return prev.filter((item) => item !== topic);
      } else {
        return [...prev, topic];
      }
    });
  };

  const handleSave = () => {
    onSave(selectedInterests);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          Select Your Interests
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {isRequired 
            ? "Please select at least one topic that interests you. You can select multiple topics."
            : "Choose topics that interest you. You can select multiple topics."}
        </Typography>
        <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 500 }}>
          Your interests will be used to recommend personalized content for you.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: "50vh", overflowY: "auto", p: 1 }}>
          <Grid container spacing={1}>
            {TOPICS.map((topic) => {
              const isSelected = selectedInterests.includes(topic);
              return (
                <Grid item key={topic}>
                  <Chip
                    label={topic}
                    onClick={() => handleToggleInterest(topic)}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>
        {selectedInterests.length > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              Selected: {selectedInterests.length} topic{selectedInterests.length !== 1 ? "s" : ""}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={false}
        >
          Save Interests
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InterestsDialog;

